import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

const SIZE = {
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
} as const;

export interface AvatarProps {
  name: string;
  src?: string;
  size?: keyof typeof SIZE;
  className?: string;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  // Track load failure so a dead URL falls back to initials instead of showing
  // a broken-image icon.
  const [failed, setFailed] = useState(false);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-100 font-semibold text-brand-800',
        SIZE[size],
        className,
      )}
      title={name}
    >
      {src && !failed ? (
        <img
          src={src}
          alt={name}
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <span aria-hidden>{initials(name)}</span>
      )}
    </span>
  );
}

/** Overlapping stack, e.g. collaborators on a row. */
export function AvatarGroup({ children }: { children: ReactNode }) {
  return <div className="flex -space-x-2 [&>*]:ring-2 [&>*]:ring-white">{children}</div>;
}
